'use client';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@wwe-portal/ui/components/button';
import { ProgressingDialogHead } from '@wwe-portal/ui/components/dialog/progressing-head';
import { usePortableDialog } from '@wwe-portal/ui/components/dialog/portable-dialog';
import { wait } from '@wwe-portal/ui/lib/timer';
import { useRouter } from 'next/navigation';
import { errorCodes } from '@wwe-portal/web/utils/constants/error-code';
import {
  isAPIError,
  useHandleCommonError,
} from '@wwe-portal/web/utils/error-handler/error';
import {
  cancelPlanNonRenew,
  checkoutPlanUpdate,
  checkoutUpgradePlanSubscribe,
} from '@wwe-portal/web/services/client/subscription';
import { SubscriptionRedirected } from '@wwe-portal/web/services/payment/type';
import { useInvalidateBalance } from '@wwe-portal/web/providers/queries/currency';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import {
  useInvalidateConsolidatedOffers,
  useInvalidateSegmentedOffers,
} from './offer';
import { usePersistentData } from '@wwe-portal/web/hooks/persistent/use-persistent-data';
import { AxiosResponse } from 'axios';
import {
  BaseSubscriptionPlan,
  CheckoutResponse,
  CurrentSubscriptionPlan,
} from '@wwe-portal/entities/subscription.type';
import { useToast } from '@wwe-portal/ui/components/toast';
import { useTranslations } from 'next-intl';
import { useInvalidateUserSubscription } from './subscription';
import { CommonDialog } from '@wwe-portal/web/components/common/dialog/common-dialog';
import { isBetterTier } from '@wwe-portal/web/utils/subscription/comparer';
import { useProcessingDialog } from '@wwe-portal/web/hooks/payment/processing-dialog';
import { useHandleXsollaPayment } from '@wwe-portal/web/hooks/payment/use-handle-xsolla-payment';
import {
  PlanChangeDialogState,
  UserSubscriptionMutationState,
} from '@wwe-portal/web/components/subscription/type';
import {
  STORAGE_ITEM_SUBSCRIPTION_SUBSCRIBE,
  STORAGE_ITEM_SUBSCRIPTION_UPDATE,
} from '@wwe-portal/web/utils/constants/keys';
import { useSubscriptionPayment } from '@wwe-portal/web/services/payment/use-subscription-payment';

interface SubscriptionMutationVariables {
  currentPlan: BaseSubscriptionPlan | null;
  plan: BaseSubscriptionPlan;
  version: string;
  cancelledPlanId?: string;
}

function isSubscriptionMutationVariables(
  data: unknown
): data is SubscriptionMutationVariables {
  return (
    typeof data === 'object' &&
    data !== null &&
    'plan' in data &&
    'version' in data
  );
}

const useSubcribingPlanState = ({ storageKey }: { storageKey: string }) =>
  usePersistentData<SubscriptionMutationVariables>(
    storageKey,
    isSubscriptionMutationVariables
  );

const getXsollaVersion = () => {
  const body = document.getElementsByTagName('body')[0];
  return body?.clientWidth && body.clientWidth > 768 ? 'desktop' : 'mobile';
};

const isSubscriptionRedirected = (error: unknown) =>
  error instanceof SubscriptionRedirected;

const useVerifyMutation = <T,>({
  getSuccessData,
  successData,
}: {
  successData?: {
    title: string;
    description: string;
  };
  getSuccessData?: (data: T) => {
    title: string;
    description: string;
  };
}) => {
  const t = useTranslations();
  const invalidate = useInvalidateUserSubscription();
  const invalidateBalance = useInvalidateBalance();
  const invalidateConsolidatedOffers = useInvalidateConsolidatedOffers();
  const invalidateSegmentedOffers = useInvalidateSegmentedOffers();
  const { toast } = useToast();
  const { showProcess, showSuccess, modal } = useProcessingDialog({
    progressData: {
      title: t('subscription_progress_title'),
      description: '',
      duration: VERIFY_DURATION_MS,
    },
    successData,
  });
  return useMutation({
    mutationFn: async (_: T) => {
      showProcess();
      await Promise.all([
        wait(VERIFY_DURATION_MS),
        invalidate(),
        invalidateBalance(),
        invalidateConsolidatedOffers(),
        invalidateSegmentedOffers(),
      ]);
    },

    onSuccess: (_, variables) => {
      if (getSuccessData) {
        showSuccess(getSuccessData(variables));
      } else {
        modal.remove();
      }
    },

    onError: () => {
      toast({ variant: 'error', content: t('subscription_verify_failed') });
    },
  });
};

const useSubscriptionError = () => {
  const handleXsollaPaymentError = useHandleXsollaPayment();
  return <T,>(
    error: unknown,
    variables: T,
    {
      onClose,
      onSettle,
      onRedirected,
    }: {
      onClose?: (variables: T) => void;
      onSettle: (option: { isHandled: boolean }) => void;
      onRedirected: () => void;
    }
  ) => {
    if (isSubscriptionRedirected(error)) {
      onRedirected();
      return;
    }
    const { isHandled, status } = handleXsollaPaymentError(error);
    // If user cancels the widget before it is loaded, do not verify subscription
    if (status !== 'cancelled') {
      onClose?.(variables);
    }
    onSettle({ isHandled });
  };
};

const useAPIError = () => {
  const t = useTranslations();
  const { toast } = useToast();
  const handleCommonError = useHandleCommonError();
  const invalidate = useInvalidateUserSubscription();
  const history = useRouter();

  const goToGame = () => {
    history.push('/play-game');
    modal.hide();
  };

  const modal = usePortableDialog(CommonDialog, {
    closable: true,
    imageElement: <ProgressingDialogHead progressPercent={0} duration={0} />,
    descriptionElement: t('subscription_please_complete_tutorial'),
    actionElement: <Button onClick={goToGame}>{t('complete_tutorial')}</Button>,
  });

  const handleAPIError = (error: unknown) => {
    if (isAPIError(error, errorCodes.TUTORIAL_NOT_COMPLETED)) {
      modal.show();
      return;
    }

    if (isAPIError(error, errorCodes.ALREADY_SUBSCRIBED)) {
      toast({
        content: t('subscription_already_subscribed'),
        variant: 'success',
      });
      invalidate();
      return;
    }

    if (isAPIError(error, errorCodes.CANNOT_DOWNGRADE)) {
      toast({
        content: t('subscription_cannot_downgrade'),
        variant: 'error',
      });
      return;
    }

    if (isAPIError(error) && error.originError.response?.data['message']) {
      toast({
        content: error.originError.response?.data['message'],
        variant: 'error',
      });
      return;
    }

    handleCommonError(error);
  };
  return {
    handleAPIError,
  };
};

export const useUpdateSubscriptinPlan = ({ handleSetteled }) => {
  const { handleAPIError } = useAPIError();
  const invalidate = useInvalidateUserSubscription();
  const handleSubscriptionError = useSubscriptionError();

  const {
    data: updatingPlan,
    clearData,
    persistData,
  } = useSubcribingPlanState({ storageKey: STORAGE_ITEM_SUBSCRIPTION_UPDATE });

  const { startPurchase } = useSubscriptionPayment({
    persistData,
    getData: () => updatingPlan,
    onPurchaseSuccess({ data }) {
      verifyMutation.mutate(data);
    },
    clearData,
  });

  const cleanMutation = () => {
    updateMutation.reset();
    verifyMutation.reset();
  };

  const cleanAll = () => {
    cleanMutation();
    clearData();
  };

  const verifyMutation = useVerifyMutation({});

  const updateMutation = useMutation({
    mutationFn: async ({
      plan,
      version,
    }: {
      plan: BaseSubscriptionPlan;
      version: string;
    }) => {
      const checkoutResponse = await checkoutPlanUpdate({
        returnUrl: window.location.href,
        version,
      });

      return await startPurchase({
        token: checkoutResponse.data.data.token,
        data: {
          plan,
          currentPlan: null,
          version,
        },
      });
    },

    onSuccess: (_, variables) => {
      verifyMutation.mutate(variables);
    },

    onError: (error, variables) => {
      handleSubscriptionError(error, variables, {
        // only clean mutation data, keep persistent data for validation when user is back to portal
        onRedirected: cleanMutation,
        onClose: () => verifyMutation.mutate(variables),
        onSettle: ({ isHandled }) => {
          if (!isHandled) handleAPIError(error);
          invalidate();
          cleanAll();
        },
      });
    },

    onSettled: handleSetteled,
  });

  return {
    isLoading: updateMutation.isPending || verifyMutation.isPending,
    updateMutation,
    verifyMutation,
  };
};

export const useSubscribePlan = (options: {
  onStartPurchase: () => void;
  onSettled: () => void;
}) => {
  const { handleAPIError } = useAPIError();
  const t = useTranslations();
  const invalidate = useInvalidateUserSubscription();

  const {
    data: subscribingPlan,
    clearData,
    persistData,
  } = useSubcribingPlanState({
    storageKey: STORAGE_ITEM_SUBSCRIPTION_SUBSCRIBE,
  });

  const cleanMutation = () => {
    subsribeMutation.reset();
    verifyMutation.reset();
  };

  const cleanAll = () => {
    cleanMutation();
    clearData();
  };

  const verifyMutation = useVerifyMutation<SubscriptionMutationVariables>({
    getSuccessData: ({ plan }) => {
      const getSuccessDescription = () => {
        if (!plan) return '';
        return t('subscription_subscribe_success_description', {
          name: plan.displayName,
        });
      };
      return {
        title: t('subscription_subscribe_success_title'),
        description: getSuccessDescription(),
      };
    },
  });

  const { startPurchase } = useSubscriptionPayment({
    persistData,
    getData: () => subscribingPlan,
    clearData,
    onPurchaseSuccess({ data }) {
      verifyMutation.mutate(data);
    },
  });

  const handleSubscriptionError = useSubscriptionError();

  const subsribeMutation = useMutation({
    mutationFn: async ({
      currentPlan,
      plan,
      version,
      cancelledPlanId,
    }: {
      currentPlan: BaseSubscriptionPlan | null;
      plan: BaseSubscriptionPlan;
      version: string;
      cancelledPlanId?: string;
    }) => {
      persistData({
        currentPlan,
        plan,
        version,
        cancelledPlanId,
      });
      let checkoutResponse: AxiosResponse<CheckoutResponse> | undefined;

      if (!currentPlan || isBetterTier(plan, currentPlan)) {
        checkoutResponse = await checkoutUpgradePlanSubscribe({
          version,
          planId: plan.id,
          returnUrl: window.location.href,
        });
      } else {
        if (cancelledPlanId) {
          await cancelPlanNonRenew(cancelledPlanId);
          options.onStartPurchase();
        }
        return;
      }
      options.onStartPurchase();
      return await startPurchase({
        token: checkoutResponse.data.data.token,
        data: { plan, currentPlan, version, cancelledPlanId },
      });
    },

    onSuccess: (_, variables) => {
      verifyMutation.mutate(variables);
    },

    onError: (error) => {
      handleSubscriptionError(error, undefined, {
        // only clean mutation data, keep persistent data for validation when user is back to portal
        onRedirected: cleanMutation,
        onSettle: ({ isHandled }) => {
          if (!isHandled) {
            handleAPIError(error);
          }
          invalidate();
          cleanAll();
        },
      });
    },
    onSettled: options.onSettled,
  });

  return {
    isLoading: subsribeMutation.isPending || verifyMutation.isPending,
    subsribeMutation,
    verifyMutation,
  };
};

export const useSubscriptionMutation = ({
  currentPlan,
}: {
  currentPlan: CurrentSubscriptionPlan | null | undefined;
}) => {
  const t = useTranslations();
  const [subcribingPlan, setSubcribingPlan] = useState<BaseSubscriptionPlan>();
  const [beingUpgradedPlan, setBeingUpgradedPlan] =
    useState<BaseSubscriptionPlan>();

  const handleSettled = () => {
    setSubcribingPlan(undefined);
    setBeingUpgradedPlan(undefined);
  };

  const subscribeFlow = useSubscribePlan({
    onStartPurchase: () => {
      upgradeDialogState?.onClose();
    },
    onSettled: handleSettled,
  });
  const updateFlow = useUpdateSubscriptinPlan({
    handleSetteled: handleSettled,
  });
  const isLoading = subscribeFlow.isLoading || updateFlow.isLoading;
  const upgradeDialogState: PlanChangeDialogState | undefined =
    beingUpgradedPlan != null && currentPlan != null
      ? {
          onClose: function (): void {
            if (isLoading) return;
            setBeingUpgradedPlan(undefined);
          },
          description: t(
            isBetterTier(beingUpgradedPlan, currentPlan)
              ? 'subscription_upgrade_description'
              : 'subscription_downgrade_description',
            {
              name: currentPlan.displayName,
            }
          ),
          buttonContent: isBetterTier(beingUpgradedPlan, currentPlan)
            ? t('subscription_upgrade_confirm')
            : null,
          onChange: () => {
            subscribeFlow.subsribeMutation.mutate(
              {
                currentPlan,
                version: getXsollaVersion(),
                plan: beingUpgradedPlan,
                cancelledPlanId: currentPlan?.subscriptionId,
              },
              {
                onSettled: handleSettled,
              }
            );
          },
          plan: beingUpgradedPlan,
        }
      : undefined;

  const mutation: UserSubscriptionMutationState = {
    subsribe: function (data: BaseSubscriptionPlan): void {
      if (currentPlan && currentPlan.id !== data.id) {
        setBeingUpgradedPlan(data);
        return;
      }
      setSubcribingPlan(data);
      if (currentPlan == null) {
        subscribeFlow.subsribeMutation.mutate(
          {
            currentPlan: currentPlan ?? null,
            version: getXsollaVersion(),
            plan: data,
          },
          {
            onSettled: handleSettled,
          }
        );
        return;
      }
      updateFlow.updateMutation.mutate({
        version: getXsollaVersion(),
        plan: currentPlan,
      });
    },
    subscribingPlan: subcribingPlan,
    isLoading,
  };

  return {
    mutation,
    upgradeDialogState,
  };
};
